import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { getUserProductWishlist, getUserCart, addProdToCart, syncLocalCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import emptywishlist from '../images/empty_wishlist.png';
import { FaHeart } from 'react-icons/fa';
import { FaArrowLeft } from "react-icons/fa";
import { IoMdCart } from "react-icons/io";
import { FaCartShopping, FaArrowRight } from "react-icons/fa6";
import { MdDelete } from "react-icons/md";
import { IoMdAdd } from "react-icons/io";

const Wishlist = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { wishlist, isLoading, isError, message, isAuthenticated, cartProducts } = useSelector((state) => state.auth);

  // Memoize cartItems to avoid unnecessary re-computations
  const cartItems = useMemo(() => {
    return isAuthenticated ? cartProducts : JSON.parse(localStorage.getItem('cart')) || [];
  }, [isAuthenticated, cartProducts]);

  // Effect 1: Fetch data only on mount or auth change (no cartProducts dep to prevent loops)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserProductWishlist());
      dispatch(getUserCart());
    }
  }, [dispatch, isAuthenticated]); // Removed cartProducts from deps

  // Effect 2: Sync localStorage cart changes (e.g., after non-auth add/remove)
  useEffect(() => {
    if (!isAuthenticated) {
      // Listen for storage changes (optional: for cross-tab sync, but basic here)
      const handleStorageChange = () => {
        // Trigger re-compute via memo (no explicit setState needed)
      };
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }
  }, [isAuthenticated]);

  const handleRemoveFromWishlist = async (prodId) => {
    const token = JSON.parse(localStorage.getItem('customer'))?.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await axios.put(
        `${base_url}user/removewishlist`,
        { prodId },
        config
      );

      if (response.status === 200) {
        toast.success('Item removed from wishlist successfully!');
        dispatch(getUserProductWishlist()); // Refresh wishlist
      } else {
        toast.error('Failed to remove item from wishlist.');
      }
    } catch (error) {
      console.error('Error removing item from wishlist:', error);
      toast.error('Something went wrong while removing the item.');
    }
  };

  const handleAddToCart = async (item) => {
    const productId = item?._id || '';
    const existingCartItem = cartItems.find(cItem => cItem?.ProductId?._id === productId); // Use memoized cartItems
    const quantity = existingCartItem?.quantity || 1;

    if (!isAuthenticated) {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      const existingIndex = localCart.findIndex(cartItem => cartItem.ProductId._id === item._id);

      if (existingIndex !== -1) {
        localCart[existingIndex].quantity += 1;
      } else {
        localCart.push({
          _id: `${item._id}_${Date.now()}`,
          ProductId: { _id: item._id, title: item.title, images: item.images },
          quantity: 1,
          price: item.CurrentPrice,
        });
      }

      localStorage.setItem('cart', JSON.stringify(localCart));
      dispatch(syncLocalCart(localCart));
      toast.success('Added to cart!');
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      console.log("✅ Token retrieved for adding to cart:", token);
      const payload = {
        ProductId: {
          _id: item._id,
          title: item.title,
          images: item.images,
          price: item.CurrentPrice,
        },
        quantity,
        price: item.CurrentPrice,
      };
      await dispatch(addProdToCart(payload)).unwrap();
      dispatch(getUserCart());
      toast.success('Added to cart!');
    } catch (err) {
      toast.error('Error adding to cart');
    }
  };

  const updateQuantityLocal = (productId, newQty) => {
    const localCart = JSON.parse(localStorage.getItem('cart')) || [];
    const updated = localCart.map(item =>
      item.ProductId._id === productId ? { ...item, quantity: newQty } : item
    );
    localStorage.setItem('cart', JSON.stringify(updated));
    dispatch(syncLocalCart(updated));
  };

  const increaseQuantity = (item) => {
    const productId = item._id;
    const cartItem = cartItems.find(cItem => cItem?.ProductId?._id === productId); // Use memoized
    const newQty = (cartItem?.quantity || 0) + 1;

    if (isAuthenticated) {
      updateQuantityAPI(productId, newQty);
    } else {
      updateQuantityLocal(productId, newQty);
    }
  };

  const decreaseQuantity = (item) => {
    const productId = item._id;
    const cartItem = cartItems.find(cItem => cItem?.ProductId?._id === productId); // Use memoized
    const newQty = Math.max(1, (cartItem?.quantity || 0) - 1);

    if (isAuthenticated) {
      updateQuantityAPI(productId, newQty);
    } else {
      updateQuantityLocal(productId, newQty);
    }
  };

  const updateQuantityAPI = async (productId, newQty) => {
    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${base_url}user/updatecart`, {
        ProductId: productId,
        quantity: newQty,
        price: (Array.isArray(wishlist) ? wishlist.find(item => item._id === productId)?.CurrentPrice : null) || 0
      }, config);
      dispatch(getUserCart());
    } catch (err) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (cartItemId) => {
    if (isAuthenticated) {
      try {
        const token = JSON.parse(localStorage.getItem('customer'))?.token;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        };

        await axios.delete(`${base_url}user/delete-product-cart`, {
          ...config,
          data: { cartItemId },
        });

        toast.success('Item removed!');
        dispatch(getUserCart());
      } catch (error) {
        console.error('Error removing item:', error);
        toast.error('Error removing item.');
      }
    } else {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      const updatedCart = localCart.filter(item => item._id !== cartItemId);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      dispatch(syncLocalCart(updatedCart));
      toast.success('Item removed from local cart!');
    }
  };

  return (
    <>
      <div className="wishlist-wrapper py-4 px-5 bg-light" style={{ minHeight: '100vh', overflow: 'auto' }}> {/* Ensure scrollable */}
        <div className="container-xxl">
          <div className="d-flex align-items-center mb-4">
            <div onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} className="back-icon me-3">
              <FaArrowLeft size={20} />
            </div>
            <h2 className="wishlist-title mb-0">Your Wishlist</h2>
          </div>

          {isLoading && (
            <div className="text-center py-4">
              <p>Loading wishlist...</p>
            </div>
          )}

          {isError && (
            <div className="text-center text-danger py-3">
              <p>{message || 'Failed to load wishlist.'}</p>
            </div>
          )}

          {!isLoading && !isError && (
            <div className="row g-3">
              {Array.isArray(wishlist) && wishlist.filter(item => item && item._id)?.length > 0 ? (
                wishlist
                  .filter(item => item && item._id)
                  .map((item, index) => {
                    const cartItem = cartItems.find(cart => cart?.ProductId?._id === item._id); // Use memoized
                    const quantity = cartItem?.quantity || 1;
                    const alreadyAdded = !!cartItem;

                    return (
                      <div className="col-12 col-sm-6 col-md-4" key={item._id || index}> {/* Use item._id as key for stability */}
                        <Link
                          to={`/product/${item._id}`}
                          className="card compact-wishlist-card d-flex flex-row align-items-center p-2 text-decoration-none"
                          style={{ color: 'inherit' }}
                        >
                          <div className="position-relative d-inline-block gap-3 me-3">
                            {/* Heart icon positioned on top-right */}
                            <FaHeart
                              className="position-absolute top-0 start-0 m-2 text-danger bg-white rounded-circle p-1"
                              title="Remove from Wishlist"
                              style={{ cursor: 'pointer', fontSize: '1.4rem', zIndex: 2 }}
                              onClick={(e) => {
                                e.preventDefault(); // prevent link click
                                handleRemoveFromWishlist(item._id);
                              }}
                            />

                            {/* Product image */}
                            <img
                              src={
                                item?.images?.[0]?.url
                                  ? `${base_url}${item.images[0].url}`
                                  : '/images/placeholder.jpg'
                              }
                              alt={item?.title || 'Product'}
                              className="wishlist-thumb image-fluid rounded"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              loading="lazy" // Lazy load to reduce layout shifts
                            />
                          </div>

                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <h5 className="mb-1">{item?.title || 'No Title'}</h5>
                            </div>
                            <h6 className="mb-2">₹ {item?.CurrentPrice?.toFixed(2) || 'N/A'}</h6>
                            {alreadyAdded ? (
                              <div className="d-flex gap-2 align-items-center">
                                <div className="d-flex align-items-center border rounded">
                                  <button 
                                    onClick={(e) => { e.preventDefault(); decreaseQuantity(item); }} 
                                    className="btn btn-sm"
                                    style={{ minWidth: '30px' }} // Prevent layout shift on click
                                  >
                                    −
                                  </button>
                                  <span className="px-2">{quantity}</span>
                                  <button 
                                    onClick={(e) => { e.preventDefault(); increaseQuantity(item); }} 
                                    className="btn btn-sm"
                                    style={{ minWidth: '30px' }}
                                  >
                                    <IoMdAdd />
                                  </button>
                                  <button 
                                    onClick={(e) => { e.preventDefault(); handleRemove(cartItem._id); }} 
                                    className="btn btn-sm text-danger"
                                    style={{ minWidth: '30px' }}
                                  >
                                    <MdDelete />
                                  </button>
                                </div>
                                <Link
                                  to="/cart"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FaCartShopping /> <FaArrowRight />
                                </Link>
                              </div>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleAddToCart(item);
                                }}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <IoMdCart /> Add to Cart
                              </button>
                            )}
                          </div>
                        </Link>
                      </div>
                    );
                  })
              ) : (
                <div className="col-12 text-center empty-state">
                  <img
                    src={emptywishlist}
                    alt="Empty Wishlist"
                    width="300"
                    className="mb-3"
                  />
                  <h4 className="fw-semibold">Explore and add your favorite items.</h4>
                  <Link to="/product" className="btn btn-primary mt-3">
                    Browse Products
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Wishlist;